// @ts-nocheck
// Small shared cells for the live-calls table and detail sheet, built on the
// realtime design primitives (cti2/team/teamUi) so the admin page, the
// team-lead board and the cti2 section all read the same.
import React from 'react';
import { ArrowDownLeft, ArrowUpRight, Repeat, Bot, Headphones, Users, ArrowRightLeft } from 'lucide-react';
import { Pill, TONE, LiveDot } from '@/features/cti2/team/teamUi';
import { Badge } from '@/components/ui/badge';
import type { LiveCall } from '../types';
import { DIRECTION_META, fmtSpan, sinceMs, statusMeta } from '../liveCallsModel';

const DIR_ICON = { IN: ArrowDownLeft, OUT: ArrowUpRight, INT: Repeat };
const DIR_TONE = { IN: 'call', OUT: 'good', INT: 'info' };

export function DirectionCell({ direction }: { direction: LiveCall['direction'] }) {
  const Icon = DIR_ICON[direction] || Repeat;
  const tone = TONE[DIR_TONE[direction] || 'off'];
  return (
    <span className="inline-flex items-center gap-2 text-[12px] font-medium">
      <span className={`grid h-6 w-6 place-items-center rounded-md ${tone.soft} ${tone.text}`}>
        <Icon size={13} />
      </span>
      {DIRECTION_META[direction]?.label || direction || '—'}
    </span>
  );
}

export function StatusPill({ call, now }: { call: LiveCall; now: number }) {
  const meta = statusMeta(call.status);
  const span = sinceMs(now, call.statusSince);
  return (
    <Pill tone={meta.tone}>
      {meta.phase === 'talking' ? <LiveDot tone="good" /> : null}
      {meta.label}
      {span !== null ? <span className="tabular-nums opacity-70">{fmtSpan(span)}</span> : null}
    </Pill>
  );
}

export function CustomerCell({ call }: { call: LiveCall }) {
  if (call.direction === 'INT') {
    return (
      <span className="text-[12px] text-muted-foreground">
        Internal · ext {call.agent?.ext || '—'} → {call.calleeAgent?.ext || '—'}
      </span>
    );
  }
  return (
    <span className="flex flex-col">
      {call.customerName ? (
        <span className="text-[12.5px]">
          <span className="font-medium">{call.customerName}</span>{' '}
          <span className="tabular-nums text-muted-foreground">{call.customerNo || '—'}</span>
        </span>
      ) : (
        <span className="text-[12.5px] font-medium tabular-nums">{call.customerNo || '—'}</span>
      )}
      <span className="text-[11px] text-muted-foreground">
        DID {call.did || '—'}
        {call.source ? ` · ${call.source.toLowerCase()}` : ''}
      </span>
    </span>
  );
}

export function AgentCell({ call }: { call: LiveCall }) {
  if (call.aiAgentId && !call.agent) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium">
        <Bot size={13} className="text-muted-foreground" /> AI agent #{call.aiAgentId}
      </span>
    );
  }
  if (!call.agent) {
    return <span className="text-[12px] text-muted-foreground">— waiting for agent</span>;
  }
  return (
    <span className="flex flex-col">
      <span className="text-[12.5px] font-medium">{call.agent.name || `Agent ${call.agent.id}`}</span>
      <span className="text-[11px] text-muted-foreground">
        ext {call.agent.ext || '—'}
        {call.calleeAgent ? ` → ${call.calleeAgent.name || 'agent'} (${call.calleeAgent.ext || '—'})` : ''}
      </span>
    </span>
  );
}

export function RouteCell({ call }: { call: LiveCall }) {
  const chips = [];
  if (call.queueName) chips.push(<Badge key="q" variant="outline">queue {call.queueName}</Badge>);
  if (call.campaignId) chips.push(<Badge key="c" variant="outline">campaign {call.campaignId}</Badge>);
  if (call.routingType && !call.queueName) chips.push(<Badge key="r" variant="secondary">{call.routingType.toLowerCase()}</Badge>);
  if (call.transfer) {
    const target = call.transfer.toAgent?.name || call.transfer.toNumber || '…';
    chips.push(
      <Badge key="t" variant="warning" className="gap-1">
        <ArrowRightLeft size={11} />
        {call.transfer.type === 'WARM' ? 'Warm' : 'Blind'} · {String(call.transfer.state || '').toLowerCase()} → {target}
      </Badge>,
    );
  }
  if (call.conference) chips.push(<Badge key="conf" variant="secondary" className="gap-1"><Users size={11} /> Conference</Badge>);
  if (call.snoopers > 0) chips.push(<Badge key="s" variant="secondary" className="gap-1"><Headphones size={11} /> {call.snoopers} listening</Badge>);
  if (!chips.length) return <span className="text-[11px] text-muted-foreground">—</span>;
  return <span className="flex flex-wrap gap-1">{chips}</span>;
}

export function DurationCell({ call, now }: { call: LiveCall; now: number }) {
  const total = sinceMs(now, call.startedAt);
  const talk = sinceMs(now, call.connectedAt);
  return (
    <span className="flex flex-col tabular-nums">
      <span className="text-[12.5px] font-medium">{fmtSpan(total)}</span>
      {talk !== null ? <span className="text-[11px] text-muted-foreground">talk {fmtSpan(talk)}</span> : null}
    </span>
  );
}
